import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { OVIZIA_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${OVIZIA_SERVICE_BASE_URL}/api/v1/admin/templates`;
const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 20;

export default function useWebsiteTemplates() {
  const authToken = sessionStorage.getItem("authToken");

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // error handler
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";

    if (err.response) {
      const status = err.response.status;

      if (status >= 500) {
        message = "Server error. Please try again later.";
      } else {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      }
    } else if (err.request) {
      message = "No response from server. Check your connection.";
    } else {
      message = err.message || message;
    }

    toast.error(message);
    console.error(`${action} error:`, err);
    setError(message);
  };

  // normalize data
  const formatTemplate = (data) => ({
    slug: data.slug,
    name: data.name,
    description: data.description,
    thumbnailUrl: data.thumbnailUrl,
    category: data.category,
    type: data.type,
    tags: data.tags || [],
    sections: data.sections || [],
    active: data.active,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  });

  const handlePaginatedResponse = (res, page = 0) => {
    const { content = [], totalPages = 1, totalElements = 0 } = res.data;

    setTemplates((prev) =>
      page === 0
        ? content.map(formatTemplate)
        : [...prev, ...content.map(formatTemplate)],
    );
    setTotalPages(totalPages);
    setTotalElements(totalElements);
  };

  // get all (paginated)
  const fetchAll = useCallback(
    async (page = DEFAULT_PAGE, size = DEFAULT_SIZE, sort = "name,asc") => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const params = { page, size, sort };
        const res = await apiClient.get(BASE_URL, { params });
        handlePaginatedResponse(res, page);
      } catch (err) {
        handleAxiosErrors(err, "fetch templates");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken],
  );

  // get by slug (useful for deep-linking straight to a template's detail)
  const getTemplateBySlug = useCallback(
    async (slug) => {
      if (!authToken || !slug) return;

      try {
        const res = await apiClient.get(`${BASE_URL}/${slug}`);
        return formatTemplate(res.data);
      } catch (err) {
        handleAxiosErrors(err, "fetch template by slug");
      }
    },
    [authToken],
  );

  // create
  const createTemplate = useCallback(
    async (payload) => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.post(BASE_URL, payload);
        const created = formatTemplate(res.data);
        setTemplates((prev) => [created, ...prev]);
        setTotalElements((prev) => prev + 1);
        toast.success("Template created");
        return created;
      } catch (err) {
        handleAxiosErrors(err, "create template");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // update
  const updateTemplate = useCallback(
    async (slug, payload) => {
      if (!authToken || !slug) return;

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.put(`${BASE_URL}/${slug}`, payload);
        const updated = formatTemplate(res.data);
        setTemplates((prev) => prev.map((t) => (t.slug === slug ? updated : t)));
        toast.success("Template updated");
        return updated;
      } catch (err) {
        handleAxiosErrors(err, "update template");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // soft delete — see TemplateAdminService on the backend for why this
  // deactivates rather than removing the row outright
  const deactivateTemplate = useCallback(
    async (slug) => {
      if (!authToken || !slug) return;

      setLoading(true);
      setError(null);

      try {
        await apiClient.delete(`${BASE_URL}/${slug}`);
        setTemplates((prev) =>
          prev.map((t) => (t.slug === slug ? { ...t, active: false } : t)),
        );
        toast.success("Template deactivated");
      } catch (err) {
        handleAxiosErrors(err, "deactivate template");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // undo a deactivate
  const reactivateTemplate = useCallback(
    async (slug) => {
      if (!authToken || !slug) return;

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.post(`${BASE_URL}/${slug}/reactivate`);
        const reactivated = formatTemplate(res.data);
        setTemplates((prev) =>
          prev.map((t) => (t.slug === slug ? reactivated : t)),
        );
        toast.success("Template reactivated");
        return reactivated;
      } catch (err) {
        handleAxiosErrors(err, "reactivate template");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  useEffect(() => {
    if (authToken) fetchAll();
  }, [authToken, fetchAll]);

  return {
    templates,
    loading,
    error,
    initialLoad,
    totalPages,
    totalElements,
    fetchAll,
    getTemplateBySlug,
    createTemplate,
    updateTemplate,
    deactivateTemplate,
    reactivateTemplate,
  };
}